import React, { useRef, useState, useEffect } from "react";
import { useParams } from "react-router-dom";

// Utilities and helpers
import { get } from "lodash";

// Redux
import { useDispatch } from "react-redux";
import genericAction from "@Actions/generic";
import downloadFile from "@Redux/Actions/downloads";

// Semantic-UI
import { Image } from "semantic-ui-react";

// Custom components
import CompanyFields from "@Components/CompanyFields";
import ConfirmModal from "@Modals/ConfirmModal";
import NotificationDialog from "@Modals/NotificationDialog";

// Styles
import styles from "./styles.module.css";

const ContactPage = () => {
	const dispatch = useDispatch();
	const companyFieldsRef = useRef(null);
	const [data, setData] = useState({});
	const [showCancelModal, setShowCancelModal] = useState(false);
	const [isSavedNotification, setIsSavedNotification] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);
	const [loading, setLoading] = useState(true);
	const [refreshKey, setRefreshKey] = useState(0);
	const [companyName, setCompanyName] = useState("");
	const [companyLogo, setCompanyLogo] = useState("");

	const { token } = useParams();

	const applyEditableAndTrack = () => {
		if (companyFieldsRef.current) {
			companyFieldsRef.current.isEditable(true);
			setHasChanges(companyFieldsRef.current.validChange());
		}
	};

	const refreshProfile = async () => {
		setLoading(true);
		const response = await dispatch(
			genericAction({
				method: "GET",
				route: `/contacts/profile/${token}`,
			})
		);

		if (response.company) {
			const { name, logo } = response.company;
			setCompanyName(name || "");
			if (logo) {
				const file = await dispatch(downloadFile({ uri: logo }));
				setCompanyLogo(file);
			}
		}

		setData(response);
		setLoading(false);

		setTimeout(applyEditableAndTrack, 300);
	};

	useEffect(() => {
		const initialize = async () => {
			await refreshProfile();

			const interval = setInterval(() => {
				if (companyFieldsRef.current) {
					setHasChanges(companyFieldsRef.current.validChange());
				}
			}, 100);

			return () => {
				return clearInterval(interval);
			};
		};

		initialize();
	}, []);

	if (loading) return <div>Cargando perfil...</div>;
	if (!data?.company) return <div>No se pudo cargar el perfil</div>;

	const handleSave = async () => {
		const wasSaved = await companyFieldsRef.current?.save();
		if (wasSaved) {
			setIsSavedNotification(true);
		}
	};

	const handleCancel = () => {
		if (companyFieldsRef.current?.validChange()) {
			setShowCancelModal(true);
		} else {
			companyFieldsRef.current?.isEditable(false);
		}
	};

	const handleConfirmCancel = async () => {
		companyFieldsRef.current?.isEditable(false);
		setShowCancelModal(false);
		await refreshProfile();
	};

	const colors = get(data, "company.settings.colors", {});
	const backgroundColor = colors.headerFormulary || "";
	const textColor = colors.fontFormulary || "";
	const buttonColor = colors.buttonFormulary || "";
	const buttonHoverColor = colors.hoverButtonsFormulary || "";

	return (
		<div className={styles.pageContainer}>
			<header className={styles.header} style={{ "--backColor": backgroundColor, "--textColor": textColor }}>
				{companyLogo && <Image src={companyLogo} alt="Logo" className={styles.headerLogo} />}
				{companyName && <h1 className={styles.headerName}>{companyName}</h1>}
			</header>

			<main className={styles.mainContent}>
				<CompanyFields
					key={refreshKey}
					ref={companyFieldsRef}
					contactData={data}
					companyFields={data.company.companyFields}
					phoneEdit
					disabledSaveButton
					disableFloatingButton
					showChannel={false}
					noSendQuestion
					disablePrintButton
					token={token}
				/>

				<div className={styles.buttonContainer}>
					<button type="button" className={styles.cancelButton} onClick={handleCancel}>
						Cancelar
					</button>
					<button
						type="submit"
						className={styles.saveButton}
						style={{ "--buttonColor": buttonColor, "--buttonHover": buttonHoverColor }}
						disabled={!hasChanges}
						onClick={handleSave}>
						Guardar
					</button>
				</div>
			</main>

			<ConfirmModal
				useContactStyles
				visible={showCancelModal}
				header="Cancelar cambios"
				content="Deseas cancelar los cambios realizados"
				buttonLabel="Sí, cancelar"
				onDismiss={() => {
					return setShowCancelModal(false);
				}}
				onConfirm={handleConfirmCancel}
				icon="warning sign"
				style={{
					"--buttonColor": buttonColor,
					"--buttonHover": buttonHoverColor,
					"--textColor": textColor,
					"--backColor": backgroundColor,
					"--fontsButtons": buttonColor,
				}}
			/>

			{isSavedNotification && (
				<NotificationDialog
					isVisible
					text="Su información fue actualizada con éxito"
					onDismiss={async () => {
						setIsSavedNotification(false);
						await refreshProfile();
						setRefreshKey((prev) => {
							return prev + 1;
						});
					}}
				/>
			)}
		</div>
	);
};

export default ContactPage;
